import { memo, useCallback, useMemo } from "react";
import sortBy from "lodash-es/sortBy";

import ContextAttachment from "components/ContextAttachment";
import CardCollapsible from "ds/components/Card/Collapsible";
import useAutoExpandOnValueChange from "ds/components/Card/Collapsible/useAutoExpandOnValueChange";
import Counter from "ds/components/Counter";
import { useToggle } from "hooks/useToggle";

import { AttachedContexts } from "../types";

type NewStackAttachedContextsProps = {
  title: string;
  contexts: AttachedContexts<boolean>;
  emptyPlaceholder: React.ReactNode;
  stackLabels: string[];
  onPriorityChange?: (id: string, value: number) => Promise<void>;
  onDetach?: (id: string) => Promise<void>;
  action?: React.ReactNode;
};

const canUpdate = { result: true };

const NewStackAttachedContextsPanel = ({
  title,
  contexts,
  stackLabels,
  onPriorityChange,
  onDetach,
  emptyPlaceholder,
  action,
}: NewStackAttachedContextsProps) => {
  const [isCollapsed, toggle] = useToggle(true);

  useAutoExpandOnValueChange(contexts.length, toggle);

  const manuallyAttachedContexts = useMemo(
    () =>
      sortBy(
        contexts.filter((context) => !context.isAutoattached),
        "priority"
      ) as AttachedContexts<false>,
    [contexts]
  );

  const autoAttachedContexts = useMemo(
    () => contexts.filter((context) => context.isAutoattached) as AttachedContexts<true>,
    [contexts]
  );

  const allContexts = useMemo(() => {
    return [...manuallyAttachedContexts, ...autoAttachedContexts];
  }, [autoAttachedContexts, manuallyAttachedContexts]);

  const handleDetach = useCallback(async (id: string) => onDetach?.(id), [onDetach]);
  const handlePriorityChange = useCallback(
    async (id: string, value: number) => onPriorityChange?.(id, value),
    [onPriorityChange]
  );

  return (
    <CardCollapsible
      ariaLevel={3}
      ariaLabel={title}
      title={title}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={<Counter count={contexts.length} />}
      headerSuffix={action}
      contentGap="medium"
    >
      {contexts.length === 0 ? (
        emptyPlaceholder
      ) : (
        <>
          {allContexts.map((context) =>
            context.isAutoattached ? (
              <ContextAttachment key={context.id} context={context} stackLabels={stackLabels} />
            ) : (
              <ContextAttachment
                key={context.id}
                context={context}
                onPriorityChange={handlePriorityChange}
                onDetach={handleDetach}
                canUpdate={canUpdate}
              />
            )
          )}
        </>
      )}
    </CardCollapsible>
  );
};

export default memo(NewStackAttachedContextsPanel);
